// NarrationBeats.tsx - the pro's narration for one street: numbered steps that reveal one at a time,
// decision steps highlighted mint. Every poker TERM inside a beat is tap-to-learn (<Term>). Ported from
// the mockup .narr / .step / renderNarr (the 90 + i*240ms stagger).
import { useEffect, useState } from 'react';
import { Term } from '../components/Glossary';
import { ArchetypeTerm } from '../components/ArchetypeTerm';
import type { ObserveBeat, Token } from './data';

/** Render one beat's tokens: prose text, tap-to-learn terms + player types, and emphasized money figures. */
function BeatTokens({ tokens }: { tokens: Token[] }) {
  return (
    <>
      {tokens.map((t, i) => {
        if (typeof t === 'string') return <span key={i}>{t}</span>;
        if ('term' in t)
          return (
            <Term key={i} term={t.term}>
              {t.text}
            </Term>
          );
        if ('archetype' in t)
          return (
            <ArchetypeTerm key={i} id={t.archetype}>
              {t.text}
            </ArchetypeTerm>
          );
        return (
          <b key={i} className="num">
            {t.num}
          </b>
        );
      })}
    </>
  );
}

export interface NarrationBeatsProps {
  beats: ObserveBeat[];
}

export function NarrationBeats({ beats }: NarrationBeatsProps) {
  // how many steps have revealed. Resets and re-staggers whenever the street's beats change. Reduced
  // motion shows them all at once (memory: prefers-reduced-motion = dial down, not strict off).
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setShown(beats.length);
      return;
    }
    setShown(0);
    const timers = beats.map((_, i) =>
      setTimeout(() => setShown((n) => Math.max(n, i + 1)), 90 + i * 240),
    );
    return () => timers.forEach(clearTimeout);
  }, [beats]);

  return (
    <div className="narr">
      {beats.map((beat, i) => (
        <div
          key={i}
          className={'step' + (beat.decision ? ' decision' : '') + (i < shown ? ' on' : '')}
        >
          <div className="no">{i + 1}</div>
          <div className="tx">
            {beat.decision && beat.tag && (
              <>
                <span className="tag">{beat.tag}</span>{' '}
              </>
            )}
            <BeatTokens tokens={beat.tokens} />
          </div>
        </div>
      ))}
    </div>
  );
}
